import { useUserData } from '@/composables/useUserData';

type Provider = 'google' | 'facebook' | 'twitch' | 'steam';

type Action = 'redirect' | 'popup';

type Params = {
  token: string;
  ts: string;
  hash: string;
  salt: string;
};

interface SocialLoginOptions {
  linkAccountSocial?: boolean;
  userId?: number;
  action?: Action;
  connectParams?: Params;
}

type Config = {
  originDomain: string;
};

export class SocialAuth {
  private static instance: SocialAuth;
  private config: Config;
  private static popup: Window | null = window;

  public static getInstance(config: Config): SocialAuth {
    if (!SocialAuth.instance) {
      SocialAuth.instance = new SocialAuth(config);
    }

    return SocialAuth.instance;
  }

  constructor(config: Config) {
    this.config = config;
  }

  /**
   * PostMessage Listener.
   * @returns void
   */
  private listenPostMessage({ source, data }: MessageEvent): void {
    if (source !== SocialAuth.popup) {
      return;
    }

    if (data === 'close') {
      this.destroyPostMessageListener();
      SocialAuth.popup?.close();
    }
  }

  /**
   * Init PostMessage listener.
   * @returns void
   */
  initPostMessageListener(): void {
    window.addEventListener('message', this.listenPostMessage, {
      passive: true,
    });
  }

  /**
   * Destroy PostMessage listener.
   * @returns void
   */
  destroyPostMessageListener(): void {
    window.removeEventListener('message', this.listenPostMessage);
  }

  /**
   * Open login popup.
   * @param url target location
   * @returns void
   */
  private openPopup(url: string, options?: string): void {
    const windowFeatures = options || 'right=10,top=10,width=1000,height=600';

    SocialAuth.popup = window.open(url, '_blank', windowFeatures);

    this.initPostMessageListener();
  }

  /**
   * Redirect to url login.
   * @param url target location
   * @returns void
   */
  private redirectTo(url: string): void {
    const { authFlowModalWithIframe } = useAuth();

    if (authFlowModalWithIframe.value) {
      window.parent.location.assign(url);
    } else {
      window.location.assign(url);
    }
  }

  /**
   * Open login popup.
   * @param provider social auth name
   * @returns void
   */
  private socialLogin(
    provider: Provider,
    options: SocialLoginOptions = {},
  ): void {
    const { originDomain } = this.config;

    if (!originDomain) {
      return;
    }

    const {
      linkAccountSocial = false,
      userId = null,
      action = 'redirect',
      connectParams,
    } = options;

    const originDomainInBase64 = btoa(originDomain);
    const originBaseHostnameInBase64 = btoa(location.hostname);
    const providerUrl = `/api/bff/social/${provider}`;

    const params = new URLSearchParams();

    params.append('od', originDomainInBase64);
    params.append('md', originBaseHostnameInBase64);
    params.append('redirect', 'true');

    if (linkAccountSocial) {
      params.append('link-account-social', 'true');
    }

    if (userId) {
      params.append('user-id', String(userId));
    }

    if (connectParams) {
      for (const [key, value] of Object.entries(connectParams)) {
        params.append(key, value);
      }
    }

    const url = `${providerUrl}?${params.toString()}`;

    if (action === 'popup') {
      this.openPopup(url);
    } else {
      this.redirectTo(url);
    }
  }

  /**
   * Provider helper methods.
   */
  public provider(provider: Provider) {
    const login = (options: SocialLoginOptions = {}) => {
      return this.socialLogin(provider, options);
    };

    const connect = (options: SocialLoginOptions = {}) => {
      const { userId } = useUserData();

      return this.socialLogin(provider, {
        ...options,
        linkAccountSocial: true,
        userId: userId.value,
      });
    };

    return {
      login,
      connect,
    };
  }
}
